import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder } from "@angular/forms";
import { forkJoin, Observable } from "rxjs";
import { ToastService } from "../../services/notification/toast.service";
import { SpinnerService } from "../../services/spinner/spinner.service";
import { OnboardingUserRoleCreateComponent } from "../onboarding-user-role-create/onboarding-user-role-create.component";
import { RbacService } from "../rbac.service";
import { UserPermissionsService } from "../../services/user-permissions.service";
import { TranslateService } from "@ngx-translate/core";
import { SecurityService } from "../../services/security.service";

@Component({
  selector: "app-onboarding-user-role-update",
  templateUrl: "./onboarding-user-role-update.component.html",
  styleUrls: ["../onboarding-user-role-create/onboarding-user-role-create.component.scss"],
  standalone: false,
})
export class OnboardingUserRoleUpdateComponent extends OnboardingUserRoleCreateComponent {
  private roleId: string | number | null = null;
  private originalPermissionIds = new Set<number>();
  private updateToast: ToastService;

  constructor(
    private updateRouter: Router,
    private updateActivatedRoute: ActivatedRoute,
    formBuilder: FormBuilder,
    toast: ToastService,
    private updateSpinner: SpinnerService,
    private updateRbacService: RbacService,
    userPermissionsService: UserPermissionsService,
    translate: TranslateService,
    securityService: SecurityService
  ) {
    super(updateRouter, updateActivatedRoute, formBuilder, toast, updateSpinner, updateRbacService, userPermissionsService, translate, securityService);
    this.updateToast = toast;
    this.roleId = this.updateActivatedRoute.snapshot.params["id"] || null;
    const role = this.updateRouter.currentNavigation()?.extras?.state?.["role"];
    if (role) {
      this.applyRoleDetail(role);
    }
  }

  override ngOnInit(): void {
    const partnerId = this.getPartnerId();
    if (partnerId && this.roleId) {
      this.updateRbacService.getRole(partnerId, this.roleId).subscribe(
        (response) => {
          const role = this.extractRoleFromResponse(response);
          if (role) {
            this.applyRoleDetail(role);
          }
          this.loadPermissionsCatalog();
        },
        () => this.loadPermissionsCatalog()
      );
      return;
    }

    this.loadPermissionsCatalog();
  }

  override save() {
    if (!this.canSaveRole()) {
      this.updateToast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionEditRoles"));
      return;
    }

    if (this.roleForm.invalid) {
      return;
    }

    const partnerId = this.getPartnerId();
    if (!partnerId || !this.roleId) {
      this.updateToast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerOrRoleSave"));
      this.goBack();
      return;
    }

    this.updateSpinner.open();
    const roleBody = {
      name: this.roleForm.value.name,
      description: this.roleForm.value.description,
    };

    this.updateRbacService.updateRole(partnerId, this.roleId, roleBody).subscribe(
      () => this.syncRolePermissions(partnerId, this.roleId),
      (error) => {
        this.updateSpinner.close();
        this.updateToast.showError(error);
      }
    );
  }

  private syncRolePermissions(partnerId: number, roleId: number | string | null) {
    if (!roleId) {
      this.updateSpinner.close();
      this.updateToast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.roleUpdated"));
      this.goBackAfterToast();
      return;
    }

    const selectedIds = new Set(this.selectedPermissions.map((permission) => Number(permission.id)));
    const permissionIdsToAdd = Array.from(selectedIds).filter((id) => !this.originalPermissionIds.has(id));
    const permissionIdsToRemove = Array.from(this.originalPermissionIds).filter((id) => !selectedIds.has(id));

    if (permissionIdsToAdd.length === 0 && permissionIdsToRemove.length === 0) {
      this.updateSpinner.close();
      this.updateToast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.roleUpdated"));
      this.goBackAfterToast();
      return;
    }

    const requests: Observable<any>[] = [];
    if (permissionIdsToAdd.length > 0) {
      requests.push(this.updateRbacService.addPermissions(partnerId, roleId, permissionIdsToAdd));
    }
    if (permissionIdsToRemove.length > 0) {
      requests.push(this.updateRbacService.removePermissions(partnerId, roleId, permissionIdsToRemove));
    }

    forkJoin(requests).subscribe(
      () => {
        this.updateSpinner.close();
        this.updateToast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.roleUpdated"));
        this.goBackAfterToast();
      },
      (error) => {
        this.updateSpinner.close();
        this.updateToast.showError(error);
      }
    );
  }

  override goBack() {
    this.updateRouter.navigate(["../../../"], {
      relativeTo: this.updateActivatedRoute,
      queryParamsHandling: "preserve",
    });
  }

  override canSaveRole(): boolean {
    return this.userPermissionsService.hasAnyPermission(this.updateRolePermissions);
  }

  private extractPermissionIdsFromRole(role: any): Set<number> {
    const ids = new Set<number>();
    if (!Array.isArray(role?.menus)) {
      return ids;
    }

    role.menus.forEach((menu: any) => {
      (menu.permissions || []).forEach((permission: any) => {
        if (permission.id) {
          ids.add(Number(permission.id));
        }
      });
    });

    return ids;
  }

  private applyRoleDetail(role: any) {
    this.roleForm.patchValue({
      name: role.name,
      description: role.description,
    });
    this.selectedPermissionIds = this.extractPermissionIdsFromRole(role);
    this.originalPermissionIds = new Set(this.selectedPermissionIds);
  }

  private extractRoleFromResponse(response: any): any | null {
    const roles = this.extractRolesFromResponse(response);
    return roles[0] || null;
  }

  private extractRolesFromResponse(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data?.roles)) return response.data.roles;
    if (Array.isArray(response?.result?.data?.roles)) return response.result.data.roles;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.roles)) return response.roles;
    return [];
  }
}
